import { SuretyAccountId, SuretyBondStatus } from "@inrev/common";
import { SuretyBondPreview, useFetchAccountBondPreviews } from "@portal/api/bond/agent";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { FilterableDataTable } from "@portal/components/ui/FilterableDataTable";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { FC, useMemo } from "react";
import { getAccountViewBondTableColumns } from "../bonds/columns";

const EMPTY_DATA: SuretyBondPreview[] = [];

type AccountBondsTableProps = {
	accountId: SuretyAccountId;
	tableName?: string;
	filterType?: "bid" | "final";
	status?: SuretyBondStatus;
};

export const AccountBondsTable: FC<AccountBondsTableProps> = ({
	accountId,
	tableName,
	filterType,
	status,
}) => {
	const {
		accountBondPreviews,
		accountBondPreviewsIsLoading,
		accountBondPreviewsError,
		refetchAccountBondPreviews,
	} = useFetchAccountBondPreviews(accountId);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const columns = useMemo(
		() => getAccountViewBondTableColumns(isMinimalUIEnabled),
		[isMinimalUIEnabled],
	);

	const filteredBonds = useMemo(() => {
		if (!accountBondPreviews) return EMPTY_DATA;
		if (!filterType && !status) return accountBondPreviews;
		return accountBondPreviews.filter((bond) => {
			if (filterType && bond.type !== filterType) return false;
			if (status && bond.status !== status) return false;
			return true;
		});
	}, [accountBondPreviews, filterType, status]);

	if (accountBondPreviewsError) {
		return (
			<ReportedRetryableLoadError
				error={accountBondPreviewsError}
				resource="bonds"
				onRetry={refetchAccountBondPreviews}
			/>
		);
	}

	return (
		<FilterableDataTable
			name={tableName ?? "account_overview_bonds"}
			columnDefs={columns}
			data={accountBondPreviewsIsLoading ? undefined : filteredBonds}
			getRowUrl={(row) => `/bonds/${row.id}`}
			enableExportToCSV
			loadingMessage="Loading bonds..."
			minHeight={400}
			maxHeight={400}
		/>
	);
};
